import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  // Session timeout: 30 minutes (30 * 60 * 1000 milliseconds)
  const SESSION_DURATION_MS = 30 * 60 * 1000;

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    clearLogoutTimer();
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminExpiresAt');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('partnerUser');
    sessionStorage.removeItem('partnerExpiresAt');
    sessionStorage.clear();
    setUser(null);
  }, [clearLogoutTimer]);

  useEffect(() => {
    // Check for existing auth on mount
    const adminToken = localStorage.getItem('adminToken');
    const adminExpiresAt = Number(localStorage.getItem('adminExpiresAt'));
    const partnerUser = sessionStorage.getItem('partnerUser');
    const partnerExpiresAt = Number(sessionStorage.getItem('partnerExpiresAt'));
    const now = Date.now();
    
    if (adminToken) {
      if (adminExpiresAt && adminExpiresAt > now) {
        const adminEmail = localStorage.getItem('adminEmail') || '';
        setUser({ type: 'admin', token: adminToken, expiresAt: adminExpiresAt, email: adminEmail });
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminExpiresAt');
        localStorage.removeItem('adminEmail');
      }
    } else if (partnerUser) {
      if (partnerExpiresAt && partnerExpiresAt > now) {
        setUser({ type: 'partner', data: JSON.parse(partnerUser), expiresAt: partnerExpiresAt });
      } else {
        sessionStorage.removeItem('partnerUser');
        sessionStorage.removeItem('partnerExpiresAt');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearLogoutTimer();

    if (!user?.expiresAt) {
      return;
    }

    const timeout = user.expiresAt - Date.now();

    if (timeout <= 0) {
      logout();
      return;
    }

    logoutTimerRef.current = setTimeout(() => {
      logout();
    }, timeout);

    return () => clearLogoutTimer();
  }, [user, logout]);

  const loginAdmin = (token, email = '') => {
    const normalizedEmail = (email || '').toString().trim().toLowerCase();
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminEmail', normalizedEmail);
    const expiresAt = Date.now() + SESSION_DURATION_MS;
    localStorage.setItem('adminExpiresAt', String(expiresAt));
    setUser({ type: 'admin', token, expiresAt, email: normalizedEmail });
  };

  const loginPartner = (partnerData) => {
    sessionStorage.setItem('partnerUser', JSON.stringify(partnerData));
    const expiresAt = Date.now() + SESSION_DURATION_MS;
    sessionStorage.setItem('partnerExpiresAt', String(expiresAt));
    setUser({ type: 'partner', data: partnerData, expiresAt });
  };

  const value = {
    user,
    loading,
    loginAdmin,
    loginPartner,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.type === 'admin',
    isPartner: user?.type === 'partner'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};